const PrivacyPolicyPage = () => {
  return (
    <div className="p-8 m-16 flex flex-col gap-y-4">
      <header className="mb-8">
        <h1 className="text-3xl font-bold">Privacy Policy</h1>
        <p className="text-sm">Last updated: December 25, 2024</p>
        <p className="text-md mt-4">By using this service you agree to the privacy policy described below.</p>
      </header>
      <article className="flex flex-col gap-y-1 mb-8">
        <h2 className="text-2xl font-bold">Interpretation and Definitions</h2>
        <p className="text-xl">For the purposes of this Privacy Policy:</p>
        <ul className="list-disc ml-8 flex flex-col gap-y-2">
          <li>
            <p>
              <strong>Account</strong> means a unique account created for You to access our Service or parts of our
              Service.
            </p>
          </li>
          <li>
            <p>
              <strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with
              a party, where &quot;control&quot; means ownership of 50% or more of the shares, equity interest or other
              securities entitled to vote for election of directors or other managing authority.
            </p>
          </li>
          <li>
            <p>
              <strong>Company</strong> (referred to as either &quot;the Company&quot;, &quot;We&quot;, &quot;Us&quot; or
              &quot;Our&quot; in this Agreement) refers to NIAEFEUP.
            </p>
          </li>
          <li>
            <p>
              <strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any other
              device by a website, containing the details of Your browsing history on that website among its many uses.
            </p>
          </li>
          <li>
            <p>
              <strong>Country</strong> refers to: Portugal
            </p>
          </li>
          <li>
            <p>
              <strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a
              digital tablet.
            </p>
          </li>
          <li>
            <p>
              <strong>Personal Data</strong> is any information that relates to an identified or identifiable
              individual.
            </p>
          </li>
          <li>
            <p>
              <strong>Service</strong> refers to the Website.
            </p>
          </li>
          <li>
            <p>
              <strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of
              the Company. It refers to third-party companies or individuals employed by the Company to facilitate the
              Service, to provide the Service on behalf of the Company, to perform services related to the Service or to
              assist the Company in analyzing how the Service is used.
            </p>
          </li>
          <li>
            <p>
              <strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the
              Service or from the Service infrastructure itself (for example, the duration of a page visit).
            </p>
          </li>
          <li>
            <p>
              <strong>Website</strong> refers to TimeTableSelector, accessible from{' '}
              <a href="tts.niaefeup.pt" rel="external nofollow noreferrer noopener" target="_blank">
                tts.niaefeup.pt
              </a>
            </p>
          </li>
          <li>
            <p>
              <strong>You</strong> means the individual accessing or using the Service, or the company, or other legal
              entity on behalf of which such individual is accessing or using the Service, as applicable.
            </p>
          </li>
        </ul>
      </article>

      <article className="flex flex-col gap-y-1">
        <h2 className="text-2xl font-bold">Collecting and Using Your Personal Data</h2>
        <h3 className="text-xl font-bold">Types of Data Collected</h3>
        <h4 className="text-lg font-bold">Personal Data</h4>
        <p>
          While using Our Service, We may ask You to provide Us with certain personally identifiable information that
          can be used to contact or identify You. Personally identifiable information may include, but is not limited
          to:
        </p>
        <ul className="list-disc ml-8 flex flex-col gap-y-2">
          <li>
            <p>University of Porto institutional email address</p>
          </li>
          <li>
            <p>First name and last name</p>
          </li>
          <li>
            <p>Student number</p>
          </li>
          <li>
            <p>Class exchange information of usage of the class exchange system.</p>
          </li>
        </ul>
        <h4 className="text-lg font-bold mt-4">Usage Data</h4>
        <p>Usage Data is collected automatically when using the Service.</p>
        <h4 className="text-lg font-bold mt-4">Tracking Technologies and Cookies</h4>
        <p>
          We use Cookies and similar tracking technologies to track the activity on Our Service and store certain
          information.
        </p>
        <ul className="list-disc ml-8 flex flex-col gap-y-2">
          <li>
            <strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on Your Device. You can
            instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do
            not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your
            browser setting so that it will refuse Cookies, our Service may use Cookies.
          </li>
        </ul>
        <p>
          Cookies can be &quot;Persistent&quot; or &quot;Session&quot; Cookies. Persistent Cookies remain on Your
          personal computer or mobile device when You go offline, while Session Cookies are deleted as soon as You close
          Your web browser. You can learn more about cookies on{' '}
          <a href="https://www.termsfeed.com/blog/cookies/#What_Are_Cookies" target="_blank" rel="noreferrer">
            TermsFeed website
          </a>{' '}
          article.
        </p>
        <p>We use only Session cookies for the purposes set out below:</p>
        <ul>
          <li>
            <p>
              <strong>Necessary / Essential Cookies</strong>
            </p>
            <p>Type: Session Cookies</p>
            <p>Administered by: Us</p>
            <p>
              Purpose: These Cookies are essential to provide You with services available through the Website and to
              enable You to use some of its features. They help to authenticate users and prevent fraudulent use of user
              accounts. Without these Cookies, the services that You have asked for cannot be provided, and We only use
              these Cookies to provide You with those services.
            </p>
          </li>
        </ul>
        <p>
          For more information about the cookies we use and your choices regarding cookies, please visit our Cookies
          Policy or the Cookies section of our Privacy Policy.
        </p>
        <h3 className="text-xl font-bold mt-4">Use of Your Personal Data</h3>
        <p>The Company may use Personal Data for the following purposes:</p>
        <ul className="list-disc ml-8 flex flex-col gap-y-2">
          <li>
            <p>
              <strong>To provide and maintain our Service</strong>
            </p>
          </li>
          <li>
            <p>
              <strong>To manage Your Account:</strong> to manage Your registration as a user of the Service. The
              Personal Data You provide can give You access to different functionalities of the Service that are
              available to You as a registered user.
            </p>
          </li>
        </ul>
        <h3 className="text-xl font-bold mt-4">Retention of Your Personal Data</h3>
        <p>
          The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this
          Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal
          obligations (for example, if we are required to retain your data to comply with applicable laws), resolve
          disputes, and enforce our legal agreements and policies.
        </p>
        <p>The Company will not retain Usage Data for internal analysis purposes.</p>
      </article>
      <h2 className="text-2xl font-bold">Changes to this Privacy Policy</h2>
      <p>
        We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy
        Policy on this page.
      </p>
      <p>
        We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective
        and update the &quot;Last updated&quot; date at the top of this Privacy Policy.
      </p>
      <p>
        You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are
        effective when they are posted on this page.
      </p>
    </div>
  )
}

export default PrivacyPolicyPage
